import { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

const FADE = '32px'

/** The mask that fades whichever edge still has content past it. */
function maskFor(before, after) {
  if (!before && !after) return undefined
  const start = before ? `transparent 0, #000 ${FADE}` : '#000 0'
  const end = after
    ? `#000 calc(100% - ${FADE}), transparent 100%`
    : '#000 100%'
  return `linear-gradient(to right, ${start}, ${end})`
}

/**
 * A row or ledger wider than its sheet scrolls sideways inside it, never
 * widening the page. While content runs past an edge, that edge fades out
 * so the reader can see there is more. `focusable` makes
 * the scroller reachable by keyboard when nothing inside it is (a table).
 * The scroller carries 4px of vertical padding (cancelled by a negative
 * margin) so focus rings inside it are not clipped.
 */
export default function EdgeScroll({
  children,
  className = '',
  innerClassName = '',
  focusable = false,
  label,
}) {
  const ref = useRef(null)
  const [edges, setEdges] = useState({ before: false, after: false })

  const measure = useCallback(() => {
    const el = ref.current
    if (!el) return
    const before = el.scrollLeft > 1
    const after = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
    setEdges(prev =>
      prev.before === before && prev.after === after ? prev : { before, after }
    )
  }, [])

  useEffect(() => {
    measure()
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    for (const child of el.children) ro.observe(child)
    return () => ro.disconnect()
  }, [measure, children])

  const mask = maskFor(edges.before, edges.after)
  const overflowing = edges.before || edges.after
  return (
    <div className={`relative min-w-0 ${className}`}>
      <div
        ref={ref}
        onScroll={measure}
        className={`relative overflow-x-auto overflow-y-hidden overscroll-x-contain py-1 -my-1 ${innerClassName}`}
        style={mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined}
        {...(focusable && overflowing
          ? { tabIndex: 0, role: 'region', 'aria-label': label }
          : {})}
      >
        {children}
      </div>
    </div>
  )
}

EdgeScroll.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  innerClassName: PropTypes.string,
  focusable: PropTypes.bool,
  // Names the scroll region for screen readers when `focusable`.
  label: PropTypes.string,
}
